import { useState } from 'react';
import { AlertCircle, Clock, Loader2, Play } from 'lucide-react';
import { useShift } from '../hooks';
import { useI18n } from '@/shared/i18n';
import { useServices } from '@/app/providers/ServiceContext';
import { useAbility } from '@/modules/identity/application/ability';

interface ShiftRequiredGuardProps {
  children: React.ReactNode;
}

export const ShiftRequiredGuard = ({ children }: ShiftRequiredGuardProps) => {
  const { t } = useI18n();
  const { activeShift, loading, actionLoading, startShift } = useShift();
  const [notes, setNotes] = useState('');
  const { currentUser } = useServices();
  const ability = useAbility();

  const canReadShift = ability.can('read', 'Shift') || ability.can('manage', 'all');
  const canStartShift = ability.can('create', 'Shift') || ability.can('manage', 'all');
  const isAdmin = ability.can('manage', 'User') || ability.can('manage', 'all');

  const handleStartShift = async () => {
    try {
      await startShift(notes || undefined);
      setNotes('');
    } catch {
      // Error handled by hook
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
      </div>
    );
  }

  if (isAdmin) {
    return <>{children}</>;
  }

  // If the user is a guard and has no active shift, prompt them to start one.
  if (canReadShift && !activeShift) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] p-8">
        <div className="bg-amber-50 bg-amber-900/20 rounded-full p-6 mb-6">
          <AlertCircle className="w-16 h-16 text-amber-500" />
        </div>

        <h2 className="text-2xl font-bold text-slate-800 text-slate-200 mb-2">
          {t('shift.required.title')}
        </h2>

        <p className="text-slate-600 text-slate-400 text-center mb-6 max-w-md">
          {t('shift.required.message')}
        </p>

        <div className="w-full max-w-sm space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-600 text-slate-400 mb-1">
              {t('shift.notes.optional')}
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={t('shift.notes.placeholder')}
              className="w-full px-3 py-2 border border-slate-200 border-slate-700 rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white bg-slate-800 text-slate-800 text-slate-200"
              rows={2}
            />
          </div>

          <button
            onClick={handleStartShift}
            disabled={actionLoading || !canStartShift}
            className="w-full flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white py-3 px-6 rounded-xl font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-green-600/30"
          >
            {actionLoading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <>
                <Play className="w-5 h-5" />
                {t('shift.required.action')}
              </>
            )}
          </button>
        </div>

        <div className="mt-8 flex items-center gap-2 text-slate-500 text-slate-400 text-sm">
          <Clock className="w-4 h-4" />
          <span>{t('shift.required.hint')}</span>
        </div>
      </div>
    );
  }

  // For non-guards, or guards with an active shift, render the content.
  return <>{children}</>;
};
